import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Switch, Box,
} from '@mui/material';
import { helpWhy } from '../../help/why';
import { IWageGrade, IWageGradeCreateRequest, IWageGradeUpdateRequest } from '../../interfaces/payroll.interface';

const toStr = (v?: number | null) => (v == null ? '' : String(v));

// Empty = "not set" (omitted from the payload); otherwise a number.
const parseOpt = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: IWageGradeCreateRequest | IWageGradeUpdateRequest) => void;
    grade: IWageGrade | null;
}

/**
 * L2: code is a user-editable label, not an enum -- but it is immutable after creation (matching
 * the backend's WageGradeUpdateDTO, which has no code field), so the unique-per-institution
 * constraint and the compliance register's catalog printout stay stable across edits.
 */
const WageGradeForm: React.FC<Props> = ({ open, onClose, onSave, grade }) => {
    const [code, setCode] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [minimumGrossWage, setMinimumGrossWage] = useState<number>(0);
    const [sortOrder, setSortOrder] = useState<number>(0);
    const [isActive, setIsActive] = useState(true);
    const [housePct, setHousePct] = useState('');
    const [medical, setMedical] = useState('');
    const [conveyance, setConveyance] = useState('');
    const [food, setFood] = useState('');
    const [increment, setIncrement] = useState('');

    useEffect(() => {
        if (grade) {
            setCode(grade.code);
            setDisplayName(grade.displayName);
            setMinimumGrossWage(grade.minimumGrossWage);
            setSortOrder(grade.sortOrder);
            setIsActive(grade.isActive);
            setHousePct(toStr(grade.housePercentOfBasic));
            setMedical(toStr(grade.medicalAllowance));
            setConveyance(toStr(grade.conveyanceAllowance));
            setFood(toStr(grade.foodAllowance));
            setIncrement(toStr(grade.annualIncrementPercent));
        } else {
            setCode('');
            setDisplayName('');
            setMinimumGrossWage(0);
            setSortOrder(0);
            setIsActive(true);
            setHousePct('');
            setMedical('');
            setConveyance('');
            setFood('');
            setIncrement('');
        }
    }, [grade, open]);

    const invalidPct = (v: string) => v.trim() !== '' && !(Number(v) >= 0 && Number(v) <= 100);
    const invalidAmt = (v: string) => v.trim() !== '' && !(Number(v) >= 0);
    const hasError = invalidPct(housePct) || invalidPct(increment)
        || invalidAmt(medical) || invalidAmt(conveyance) || invalidAmt(food);

    const saveWhy = hasError ? 'Fix the highlighted percentage or amount first.'
        : !displayName.trim() ? 'Type a display name first.'
        : (!grade && !code.trim()) ? 'Type a code first.' : null;

    const handleSubmit = () => {
        if (hasError) return;
        const composition = {
            housePercentOfBasic: parseOpt(housePct),
            medicalAllowance: parseOpt(medical),
            conveyanceAllowance: parseOpt(conveyance),
            foodAllowance: parseOpt(food),
            annualIncrementPercent: parseOpt(increment),
        };
        if (grade) {
            onSave({ displayName, minimumGrossWage, sortOrder, isActive, ...composition } as IWageGradeUpdateRequest);
        } else {
            onSave({ code, displayName, minimumGrossWage, sortOrder, ...composition } as IWageGradeCreateRequest);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.gradeForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>{grade ? 'Edit' : 'Add'} Wage Grade</DialogTitle>
            <DialogContent data-help-section="payroll.gradeForm.section.details">
                <TextField
                    autoFocus={!grade}
                    margin="dense"
                    data-help="payroll.gradeForm.field.code" label="Code"
                    fullWidth
                    value={code}
                    disabled={!!grade}
                    helperText={grade ? 'Code cannot be changed after creation' : 'e.g. GRADE_1, LEARNER'}
                    onChange={(e) => setCode(e.target.value)}
                    sx={{ mb: 2, mt: 1 }}
                />
                <TextField
                    margin="dense"
                    data-help="payroll.gradeForm.field.displayName" label="Display Name"
                    fullWidth
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    data-help="payroll.gradeForm.field.minWage" label="Minimum Gross Wage"
                    type="number"
                    fullWidth
                    value={minimumGrossWage}
                    onChange={(e) => setMinimumGrossWage(parseFloat(e.target.value) || 0)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    data-help="payroll.gradeForm.field.sortOrder" label="Sort Order"
                    type="number"
                    fullWidth
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <TextField
                        margin="dense" data-help="payroll.gradeForm.field.housePct" label="House Rent % of Basic" type="number"
                        value={housePct} onChange={(e) => setHousePct(e.target.value)}
                        error={invalidPct(housePct)} helperText={invalidPct(housePct) ? 'Must be between 0 and 100' : undefined}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <TextField
                        margin="dense" data-help="payroll.gradeForm.field.medical" label="Medical Allowance" type="number"
                        value={medical} onChange={(e) => setMedical(e.target.value)}
                        error={invalidAmt(medical)} helperText={invalidAmt(medical) ? 'Must be zero or greater' : undefined}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <TextField
                        margin="dense" data-help="payroll.gradeForm.field.conveyance" label="Transport (Conveyance) Allowance" type="number"
                        value={conveyance} onChange={(e) => setConveyance(e.target.value)}
                        error={invalidAmt(conveyance)} helperText={invalidAmt(conveyance) ? 'Must be zero or greater' : undefined}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <TextField
                        margin="dense" data-help="payroll.gradeForm.field.food" label="Food Allowance" type="number"
                        value={food} onChange={(e) => setFood(e.target.value)}
                        error={invalidAmt(food)} helperText={invalidAmt(food) ? 'Must be zero or greater' : undefined}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                    <TextField
                        margin="dense" data-help="payroll.gradeForm.field.increment" label="Annual Increment %" type="number"
                        value={increment} onChange={(e) => setIncrement(e.target.value)}
                        error={invalidPct(increment)} helperText={invalidPct(increment) ? 'Must be between 0 and 100' : undefined}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                </Box>
                {grade && (
                    <FormControlLabel
                        control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                        data-help="payroll.gradeForm.field.active" label="Active"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.gradeForm.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="payroll.gradeForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSubmit} variant="contained"
                        disabled={!displayName.trim() || (!grade && !code.trim()) || hasError}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WageGradeForm;
